import { useCallback, useMemo } from 'react';

/**
 * Custom hook to efficiently check if dates or times are disabled.
 *
 * Creates an optimized lookup mechanism using Set for O(1) performance
 * and provides a function to check if any given item is disabled.
 * Handles both Date objects and string types consistently.
 *
 * @param disabledItems - Array of disabled dates or times (Date | string)
 * @returns Function to check if a given item is disabled
 */
export function useDateTimeBlockDisabledItems(
  disabledItems?: Array<Date | string>,
): (item: Date | string) => boolean {
  const disabledItemsSet = useMemo(() => {
    if (!disabledItems) return new Set();

    return new Set(disabledItems.map((item) => (item instanceof Date ? item.getTime() : item)));
  }, [disabledItems]);

  const isItemDisabled = useCallback(
    (item: Date | string): boolean => {
      const itemKey = item instanceof Date ? item.getTime() : item;
      return disabledItemsSet.has(itemKey);
    },
    [disabledItemsSet],
  );

  return isItemDisabled;
}
